import Bluebird from 'bluebird'
import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  IForm_File,
} from '@kaidam/fc-shared/src/types/schema/form'

import {
  getFilestoreUploadUrl,
} from '@kaidam/fc-shared/src/utils/url'

import {
  IField_Filestore_Type,
  IField_Storage_Location,
  IField_Storage_Access_Type,
} from '@kaidam/fc-shared/src/types/storage'

import {
  getParent,
} from '@kaidam/fc-shared/src/utils/storage'

import {
  FilestoreState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  PayloadUploadFiles,
  PayloadAddFolder,
  PayloadRename,
} from '../../types/filestore'

import {
  NetworkThunk,
} from '../utils/network'

import {
  getProjectInfo,
} from '../utils/api'

import projectSelectors from '../selectors/project'
import fileuploadActions, { apiUploader } from './fileupload'
import snackbarActions from './snackbar'

const prefix = 'filestore'

/*

  initial state

*/
const initialState: FilestoreState = {
  list: [],
}

/*

  reducers

*/
const setList: ReducerHandler<FilestoreState, IForm_File[]> = (state, action) => {
  state.list = action.payload
}

/*

  thunks

*/
const list: ThunkHandler<IField_Storage_Location, IForm_File[]> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const orgid = projectSelectors.currentOrgId(getState())
  const data = await handlers.get<IForm_File[]>(`/filestore/${orgid}/${query.type}`, {
    params: {
      path: query.path || '',
    }
  })
  dispatch(actions.setList(data))
  return data
}

const uploadFiles: ThunkHandler<PayloadUploadFiles, IForm_File[]> = (payload) => async (dispatch, getState) => {
  dispatch(fileuploadActions.startUploads(payload))
  const info = getProjectInfo(getState)
  const url = getFilestoreUploadUrl(info.project, info.org, payload.location.type)
  try {
    const results = await Bluebird.map(payload.files, async file => apiUploader({
      url,
      file,
      path: payload.location.path,
      onProgress: (values: ProgressEvent) => {
        dispatch(fileuploadActions.setUploadedProgress({
          filename: file.name,
          values,
        }))
      },
    }))
    dispatch(fileuploadActions.reset())
    return results
  } catch(e) {
    dispatch(fileuploadActions.reset())
    return []
  }
}

const upload: ThunkHandler<PayloadUploadFiles, boolean> = (payload) => async (dispatch, getState) => {
  const results = await dispatch(actions.uploadFiles(payload))
  return results && results.length > 0
}

const addFolder: ThunkHandler<PayloadAddFolder, boolean> = (query) => async (dispatch, getState) => {
  if(!query.name) throw new Error(`no name given`)
  const handlers = getProjectHandlers(getState)
  const orgid = projectSelectors.currentOrgId(getState())
  const path = query.location.path ?
    query.location.path + '/' + query.name :
    query.name
  await handlers.post(`/filestore/${orgid}/${query.location.type}/folders`, {}, {
    params: {
      path: path.replace(/\/+/g, '/')
    }
  })
  await dispatch(actions.list(query.location))
  dispatch(snackbarActions.setSuccess(`folder created`))
  return true
}

const rename: ThunkHandler<PayloadRename, boolean> = (query) => async (dispatch, getState) => {
  if(!query.renameTo) throw new Error(`no name given`)
  throw new Error(`this feature is currently disabled`)
  const handlers = getProjectHandlers(getState)
  const orgid = projectSelectors.currentOrgId(getState())
  await handlers.put(`/filestore/${orgid}/${query.location.type}/name`, {}, {
    params: {
      path: query.location.path,
      renameTo: query.renameTo,
    }
  })
  await dispatch(actions.list(Object.assign({}, query.location, {
    path: getParent(query.location.path),
  })))
  dispatch(snackbarActions.setSuccess(`item renamed`))
  return true
}

const deleteItem: ThunkHandler<IField_Storage_Location, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const orgid = projectSelectors.currentOrgId(getState())
  await handlers.delete(`/filestore/${orgid}/${query.type}`, {
    params: {
      path: query.path,
    }
  })
  await dispatch(actions.list(Object.assign({}, query, {
    path: getParent(query.path),
  })))
  dispatch(snackbarActions.setSuccess(`item deleted`))
  return true
}

const copyFileToOtherDrive: ThunkHandler<{
  file: IForm_File,
  type: IField_Storage_Access_Type,
}, IForm_File> = ({
  file,
  type,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const orgid = projectSelectors.currentOrgId(getState())
  const result = await handlers.post<IForm_File, IForm_File>(`/filestore/${orgid}/copy/${type}`, file)
  return result
}

/*

  collections

*/
const reducers = {
  setList,
}

export const actions = {
  setList: CreateAction(prefix, `setList`, setList),
  list: NetworkThunk(prefix, 'list', list, {
    snackbar: true,
  }),
  uploadFiles: NetworkThunk(prefix, 'uploadFiles', uploadFiles, {
    snackbar: true,
  }),
  upload: NetworkThunk(prefix, 'upload', upload, {
    snackbar: true,
  }),
  addFolder: NetworkThunk(prefix, 'addFolder', addFolder, {
    snackbar: true,
    globalLoading: {
      message: 'creating folder...'
    },
  }),
  rename: NetworkThunk(prefix, 'rename', rename, {
    snackbar: true,
    globalLoading: {
      message: 'renaming item...'
    },
  }),
  deleteItem: NetworkThunk(prefix, 'deleteItem', deleteItem, {
    snackbar: true,
    globalLoading: {
      message: 'deleting item...'
    },
  }),
  copyFileToOtherDrive: NetworkThunk(prefix, 'copyFileToOtherDrive', copyFileToOtherDrive, {
    snackbar: true,
    globalLoading: {
      message: 'copying file...'
    },
  }),
}

export const reducer = CreateReducer<FilestoreState>({
  prefix,
  initialState,
  reducers,
})

export default actions